/**
 * Smart Product Finder Brain - Streamlined & Efficient
 * 
 * Requirements:
 * - Smart threshold: Show results when ≤5 products OR no more filter options available
 * - Maximum 8 filter steps (absolute limit)
 * - Flexible button ordering with manual override
 * - Graceful error handling to ensure all products remain findable
 * - Enhanced final results with "See More Products" links
 * - Data-driven decisions based on actual product-data.json
 */

class SmartProductFinderBrain {
  constructor() {    // Core Configuration - Manual Override Capable
    this.config = {
      maxSteps: 8,
      targetResults: 5,
      debugMode: false, // Toggle for console logging - controlled by section setting
      manualButtonOrder: true, // Use predefined order vs auto-determination
        // Manual Button Order (Priority 8-1, highest first)
      buttonOrder: [
        { key: 'tags', type: 'school_level', label: 'School Level', priority: 8 },
        { key: 'custom.subject', type: 'subject', label: 'Subject', priority: 7 },
        { key: 'custom.elective_category', type: 'elective_category', label: 'Elective Category', priority: 6.5, 
          condition: (filters) => filters['custom.subject'] === 'Electives & Enrichment' },
        { key: 'custom.product_type', type: 'product_type', label: 'Product Type', priority: 6 },
        { key: 'custom.course_type', type: 'course_type', label: 'Course Type', priority: 5 },
        { key: 'vendor', type: 'curriculum', label: 'Curriculum', priority: 4 },
        { key: 'custom.grade_level', type: 'grade_level', label: 'Specific Grade', priority: 3 },
        { key: 'custom.learning_style', type: 'learning_style', label: 'Learning Style', priority: 2 },
        { key: 'more_filters', type: 'more_filters', label: 'Optional Filters', priority: 1 }
      ],
      
      // More Filters Dropdown Options
      moreFiltersOptions: [        { key: 'tags', type: 'product_format', label: 'Format', tags: ['Reader'] },
        { key: 'tags', type: 'popularity', label: 'Best Sellers', tags: ['Best Seller'] },
        { key: 'tags', type: 'career_focus', label: 'Career Focus', tags: ['Careers For Grades 6+'] },
        { key: 'tags', type: 'career_education', label: 'Career Education', tags: ['CTE'] },
        { key: 'tags', type: 'stem_focus', label: 'STEM Focus', tags: ['STEM'] },
        { key: 'tags', type: 'college_prep', label: 'College Preparation', tags: ['College Readiness'] }
      ]
    };
      // Tag-based filter definitions (from your product data analysis)
    this.tagFilters = {
      school_level: ['Elementary School', 'Middle School', 'High School'],      grade_range: ['Grades Pre K-2', 'Grades 3-5', 'Grades 6-8', 'Grades 9-10', 'Grades 11-12'],
      availability: ['Released'],
      brand: ['Monarch and Lifepac']
    };
    
    // Elective categorization mapping for "Electives & Enrichment" subject
    this.electiveCategories = {
      'Career & Life Skills': {
        keywords: ['career', 'business', 'economics', 'finance', 'accounting', 'entrepreneurship', 'life skills', 'personal finance', 'consumer economics'],
        description: 'Career preparation, business, economics, and life skills courses'
      },
      'Arts & Creative': {
        keywords: ['art', 'music', 'drama', 'creative writing', 'theater', 'dance', 'photography', 'film', 'graphic design', 'studio art'],
        description: 'Visual arts, music, drama, and creative expression courses'
      },
      'Technology & Computer': {
        keywords: ['computer', 'technology', 'programming', 'coding', 'digital', 'software', 'web design', 'information technology'],
        description: 'Computer science, programming, and technology courses'
      },
      'Languages': {
        keywords: ['spanish', 'french', 'latin', 'sign language', 'chinese', 'german', 'italian', 'japanese', 'foreign language'],
        description: 'World languages and communication courses'
      },
      'Health & Physical': {
        keywords: ['health', 'physical education', 'pe', 'fitness', 'nutrition', 'wellness', 'sports', 'exercise'],
        description: 'Health, physical education, and wellness courses'
      },
      'Advanced Studies': {
        keywords: ['ap', 'advanced placement', 'honors', 'dual enrollment', 'college level', 'advanced'],
        description: 'Advanced placement and honors level courses'
      },
      'Special Interest': {
        keywords: ['psychology', 'philosophy', 'sociology', 'journalism', 'communication', 'leadership', 'study skills'],
        description: 'Psychology, social sciences, and other specialized subjects'
      }
    };
      // Performance optimization with smart cache invalidation
    this.cache = new Map();
    this.maxCacheSize = 100;
    this.stepHistory = [];
    
    // Smart cache invalidation system
    this.filterHistory = []; // Track filter progression for depth-aware caching
    this.cacheInvalidationLevel = 0; // Track current filter depth for cache invalidation
    
    // Error handling state
    this.errorRecoveryMode = false;
    this.fallbackFilters = ['vendor', 'custom.product_type'];
  }

  /**
   * Debug logging (toggleable)
   */
  log(message, data = null) {
    if (this.config.debugMode) {
      console.log(`🧠 Brain: ${message}`, data || '');
    }
  }  /**
   * Main delegation method - determines next filter step with smart result display
   */  updateNextFilter(context) {
    const { nextFilter, filterDropdownButton, filterDropdownMenu, allProducts, currentFilters, selectDropdownOption } = context;
    
    try {
      const filteredProducts = this.getFilteredProducts(allProducts, currentFilters);
      const currentStep = this.stepHistory.length;
      
      this.log(`Update next filter - Step ${currentStep + 1}, ${filteredProducts.length} products`);
      
      // Stop if skip option was explicitly selected (user chose to skip optional filters)
      if (currentFilters.skip_optional_filters === 'skip_optional_filters') {
        nextFilter.classList.add('hidden');
        return false;
      }
      
      // Get next filter step to check if more options are available
      const nextStepData = this.getNextFilterStep(filteredProducts, currentFilters, currentStep);
      
      // Stop if no more filtering options available OR we've reached absolute max steps
      if (!nextStepData || currentStep >= this.config.maxSteps) {
        this.log(nextStepData ? 'Reached max steps' : 'No more valid filter steps available');
        nextFilter.classList.add('hidden');
        return false;
      }
      
      // Always show next filter step if available, regardless of product count
      // This allows users to continue filtering even with small result sets
      this.renderFilterStep(nextStepData, filteredProducts, { nextFilter, filterDropdownButton, filterDropdownMenu, selectDropdownOption });
      
      // Track step history
      this.stepHistory.push(nextStepData);
      
      return true;
      
    } catch (error) {
      this.log('Error in updateNextFilter', error);
      this.handleError(error, context);
      return false;
    }
  }
  /**
   * Determine the next filter step to show
   */
  getNextFilterStep(products, currentFilters, stepIndex) {
    // Get available filter definitions
    const availableFilters = this.getAvailableFilters(currentFilters);
      if (this.config.manualButtonOrder) {
      // Use predefined button order
      for (const buttonDef of this.config.buttonOrder) {
        if (this.isFilterAlreadyApplied(buttonDef, currentFilters)) continue;
        
        // Special handling for Optional Filters (more_filters)
        if (buttonDef.key === 'more_filters') {
          // Check if Optional Filters would only have the skip option
          if (this.hasOnlySkipOptionForOptionalFilters(products, currentFilters)) {
            // Skip Optional Filters entirely - no actual optional filters are available
            this.log('Skipping Optional Filters - only skip option would be available');
            continue;
          }
        }
        
        const options = this.getFilterOptions(buttonDef, products, currentFilters);
        if (options.length >= 2 || (options.length === 1 && this.isValidSingleOption(buttonDef, options[0]))) {
          return {
            ...buttonDef,
            options: options,
            stepIndex: stepIndex
          };
        }
      }
    } else {
      // Auto-determine best order based on impact
      const scoredFilters = availableFilters.map(filter => ({
        ...filter,
        options: this.getFilterOptions(filter, products, currentFilters),
        impact: this.calculateImpact(filter, products)
      })).filter(f => f.options.length >= 1)
        .sort((a, b) => b.impact - a.impact);
      
      if (scoredFilters.length > 0) {
        return {
          ...scoredFilters[0],
          stepIndex: stepIndex
        };
      }
    }
    
    return null;
  }

  /**
   * Get available filters excluding already applied ones
   */
  getAvailableFilters(currentFilters) {
    return this.config.buttonOrder.filter(filter => 
      !this.isFilterAlreadyApplied(filter, currentFilters)
    );
  }  /**
   * Check if filter is already applied
   */  isFilterAlreadyApplied(filterDef, currentFilters) {
    // Check conditional filter - don't show if condition not met
    if (filterDef.condition && !filterDef.condition(currentFilters)) {
      return true; // Treat as "already applied" to skip it
    }
    
    if (filterDef.key === 'tags') {
      // For tag filters, check if any of the tag values are selected
      return filterDef.tags?.some(tag => 
        Object.values(currentFilters).includes(tag)
      ) || currentFilters.tags !== undefined;
    } else if (filterDef.key === 'more_filters') {
      // More filters is considered "applied" if:
      // 1. Skip option was selected, OR
      // 2. User has selected any optional filter tags
      return currentFilters.skip_optional_filters === 'skip_optional_filters' ||
             (currentFilters.tags && currentFilters.tags.length > 0);
    } else {
      // Direct key match
      return currentFilters[filterDef.key] !== undefined;
    }
  }/**
   * Get filter options for a given filter definition with depth-aware caching
   */
  getFilterOptions(filterDef, products, currentFilters = {}) {
    // Create depth-aware cache key that includes filter progression level
    const filterDepth = this.filterHistory.length;
    const cacheKey = `${filterDef.key}_${filterDef.type}_${products.length}_depth_${filterDepth}`;
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }
      let options = [];
    
    if (filterDef.key === 'tags' && filterDef.type === 'school_level') {
      // School level filtering
      options = this.getTagOptions(this.tagFilters.school_level, products);
    } else if (filterDef.key === 'tags' && this.tagFilters[filterDef.type]) {
      // Other tag-based filters
      options = this.getTagOptions(this.tagFilters[filterDef.type], products);
    } else if (filterDef.key === 'custom.elective_category') {
      // Elective category filtering - only when "Electives & Enrichment" is selected
      options = this.getElectiveCategoryOptions(products);
    } else if (filterDef.key === 'more_filters') {
      // More filters dropdown - pass currentFilters to exclude already applied tags
      options = this.getMoreFiltersOptions(products, currentFilters);
    } else {
      // Metafield-based filters
      options = this.getMetafieldOptions(filterDef.key, products);
    }
    
    // Cache the result with depth-aware key
    this.cacheSet(cacheKey, options);
    return options;
  }

  /**
   * Get options for tag-based filters
   */
  getTagOptions(tags, products) {
    const options = [];
    
    tags.forEach(tag => {
      const count = products.filter(product => 
        product.tags && product.tags.some(ptag => 
          ptag.toLowerCase().trim() === tag.toLowerCase().trim()
        )
      ).length;
      
      if (count > 0) {
        options.push({ value: tag, count: count });
      }
    });
    
    return options.sort((a, b) => b.count - a.count);
  }  /**
   * Get options for metafield-based filters with enhanced debug logging
   */
  getMetafieldOptions(key, products) {
    // For grade level, use simulation approach to match actual filtering logic
    if (key === 'custom.grade_level') {
      return this.getGradeLevelOptionsWithSimulation(products);
    }
    
    const valueCounts = {};
    
    products.forEach(product => {
      const value = product[key];
      if (value && value.toString().trim()) {
        const cleanValue = this.cleanMetafieldValue(value.toString().trim());
        if (cleanValue) {
          valueCounts[cleanValue] = (valueCounts[cleanValue] || 0) + 1;
        }
      }
    });
    
    const options = Object.entries(valueCounts)
      .filter(([_, count]) => count > 0)
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count);
    
    return options;
  }

  /**
   * Get grade level options by simulating actual filtering logic
   */
  getGradeLevelOptionsWithSimulation(products) {
    this.log(`\n=== SIMULATING GRADE LEVEL FILTERING ===`);
    this.log(`Processing ${products.length} products for grade level options`);
    
    // Get all unique grade levels mentioned in products
    const allGrades = new Set();
    products.forEach(product => {
      const value = product['custom.grade_level'];
      if (value && value.toString().trim()) {
        const cleanValue = this.cleanMetafieldValue(value.toString().trim());
        if (cleanValue) {
          allGrades.add(cleanValue);
        }
      }
    });
    
    // Add standard Full Access grades if we have Full Access products
    const hasFullAccessProducts = products.some(p => this.isEnhancedFullAccessProduct(p));
    if (hasFullAccessProducts) {
      const fullAccessGrades = [
        'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8',
        'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'
      ];
      fullAccessGrades.forEach(grade => allGrades.add(grade));
    }
    
    // Simulate filtering for each grade to get accurate counts
    const gradeCounts = {};
      Array.from(allGrades).forEach(grade => {
      // Simulate filtering with this grade level
      const simulatedCount = products.filter(product => {
        // Apply the same logic used in getFilteredProducts
        if (this.isEnhancedFullAccessProduct(product)) {
          // Full Access products match grades 3-12
          const filterValue = grade.toLowerCase(); // This represents the user's grade selection
          const fullAccessGrades = ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
          
          if (fullAccessGrades.some(gradeNum => filterValue.includes(gradeNum))) {
            this.log(`  Full Access product "${product.title}" matches ${grade}`);
            return true;
          }
        }
        
        // Standard grade matching
        const productGrade = product['custom.grade_level'];
        if (productGrade) {
          const cleanProductGrade = this.cleanMetafieldValue(productGrade.toString());
          const matches = cleanProductGrade && 
                         cleanProductGrade.toLowerCase().includes(grade.toLowerCase());
          if (matches) {
            this.log(`  Regular product "${product.title}" matches ${grade}`);
          }
          return matches;
        }
        
        return false;
      }).length;
      
      if (simulatedCount > 0) {
        gradeCounts[grade] = simulatedCount;
        this.log(`📊 ${grade}: ${simulatedCount} products would match`);
      }
    });
    
    const options = Object.entries(gradeCounts)
      .filter(([_, count]) => count > 0)
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count);
    
    this.log(`Grade level options found:`, options);
    return options;
  }/**
   * Get options for "Optional Filters" dropdown with skip option
   */
  getMoreFiltersOptions(products, currentFilters = {}) {
    const options = [];
    
    // Add "These do not apply to me" as the first option
    options.push({
      value: 'skip_optional_filters',
      count: products.length,
      label: 'These do not apply to me',
      filterKey: 'skip',
      isSkipOption: true
    });
    
    // Get currently applied filter values to exclude them
    const appliedValues = Object.values(currentFilters || {});        this.config.moreFiltersOptions.forEach(filterDef => {
      if (filterDef.tags) {
        filterDef.tags.forEach(tag => {
          // Skip if this tag is already applied as a filter
          if (appliedValues.includes(tag)) {
            return;
          }
          
          // FIXED: Use consistent filtering logic for count calculation
          // This should match the logic in getFilteredProducts()
          const count = products.filter(product => 
            product.tags && product.tags.some(productTag => 
              productTag.toLowerCase().includes(tag.toLowerCase())
            )
          ).length;
          
          if (count > 0) {
            options.push({ 
              value: tag, 
              count: count,
              label: filterDef.label,
              filterKey: filterDef.key
            });
          }
        });
      }
    });
    
    return options;
  }
  /**
   * Check if Optional Filters would only have the skip option (no actual filters available)
   */
  hasOnlySkipOptionForOptionalFilters(products, currentFilters = {}) {
    const options = this.getMoreFiltersOptions(products, currentFilters);
    
    // If only 1 option exists and it's the skip option, then no actual optional filters are available
    return options.length === 1 && options[0].isSkipOption;
  }

  /**
   * Get elective category options by analyzing product titles and content
   */
  getElectiveCategoryOptions(products) {
    const categoryCounts = {};
    
    // Initialize all categories with zero count
    Object.keys(this.electiveCategories).forEach(category => {
      categoryCounts[category] = 0;
    });
      // Track products that match multiple categories to avoid double-counting
    const productCategoryMatches = new Map();
    
    // Analyze each product to determine which elective categories it belongs to
    products.forEach(product => {
      const title = (product.title || '').toLowerCase();
      const description = (product.description || '').toLowerCase();
      const subject = (product['custom.subject'] || '').toLowerCase();
      const combinedText = `${title} ${description} ${subject}`;
      
      let matchedCategories = [];
      
      // Check which categories this product matches
      Object.entries(this.electiveCategories).forEach(([categoryName, categoryData]) => {
        const matches = categoryData.keywords.some(keyword => 
          combinedText.includes(keyword.toLowerCase())
        );
        
        if (matches) {
          matchedCategories.push(categoryName);
        }
      });
      
      // Count the product in the first matching category to avoid double-counting
      // This ensures sum of category counts matches the total filtered products
      if (matchedCategories.length > 0) {
        const primaryCategory = matchedCategories[0]; // Use first match as primary
        categoryCounts[primaryCategory]++;
        productCategoryMatches.set(product.title, matchedCategories);
        this.log(`Product "${product.title}" categorized as: ${primaryCategory}${matchedCategories.length > 1 ? ` (also matches: ${matchedCategories.slice(1).join(', ')})` : ''}`);
      } else {
        this.log(`⚠️  Product "${product.title}" did not match any elective category`);
      }
    });
      // Check for uncategorized products to help debug count discrepancies
    const uncategorized = this.getUncategorizedElectiveProducts(products);
    const totalCategorized = Object.values(categoryCounts).reduce((sum, count) => sum + count, 0);
      this.log(`📊 Elective categorization summary:`, {
      totalProducts: products.length,
      totalCategorized: totalCategorized,
      uncategorized: uncategorized.length,
      categoryBreakdown: categoryCounts,
      multiMatchProducts: Array.from(productCategoryMatches.entries()).filter(([_, matches]) => matches.length > 1).length
    });
    
    // Add "Other Electives" category for uncategorized products if any exist
    if (uncategorized.length > 0) {
      categoryCounts['Other Electives'] = uncategorized.length;
      this.log(`Added "Other Electives" category with ${uncategorized.length} products`);
    }
      // Convert to options array with counts > 0
    const options = Object.entries(categoryCounts)
      .filter(([_, count]) => count > 0)
      .map(([category, count]) => ({
        value: category,
        count: count,
        description: this.electiveCategories[category]?.description || 'Other elective courses not covered by specific categories'
      }))
      .sort((a, b) => b.count - a.count);
      
    this.log(`Elective category options:`, options);
    return options;
  }  /**
   * Check if a product matches a specific elective category based on keyword analysis
   */
  matchesElectiveCategory(product, categoryName) {
    // Handle "Other Electives" catch-all category
    if (categoryName === 'Other Electives') {
      // Check if this product doesn't match any of the defined categories
      const title = (product.title || '').toLowerCase();
      const description = (product.description || '').toLowerCase();
      const subject = (product['custom.subject'] || '').toLowerCase();
      const combinedText = `${title} ${description} ${subject}`;
      
      const matchesAnyDefinedCategory = Object.entries(this.electiveCategories).some(([_, categoryData]) => {
        return categoryData.keywords.some(keyword => 
          combinedText.includes(keyword.toLowerCase())
        );
      });
      
      return !matchesAnyDefinedCategory;
    }
    
    if (!this.electiveCategories[categoryName]) {
      return false;
    }
    
    const keywords = this.electiveCategories[categoryName].keywords;
    const title = (product.title || '').toLowerCase();
    const description = (product.description || '').toLowerCase();
    const subject = (product['custom.subject'] || '').toLowerCase();
    const searchText = `${title} ${description} ${subject}`;
    
    // Check if any keyword from the category appears in the product title, description, or subject
    return keywords.some(keyword => searchText.includes(keyword.toLowerCase()));
  }

  /**
   * Diagnostic method to identify uncategorized elective products
   * This helps debug count discrepancies between total and categorized products
   */
  getUncategorizedElectiveProducts(products) {
    const uncategorized = [];
    
    products.forEach(product => {
      const title = (product.title || '').toLowerCase();
      const description = (product.description || '').toLowerCase();
      const subject = (product['custom.subject'] || '').toLowerCase();
      const combinedText = `${title} ${description} ${subject}`;
      
      // Check if this product matches any elective category
      const matchesAnyCategory = Object.entries(this.electiveCategories).some(([categoryName, categoryData]) => {
        return categoryData.keywords.some(keyword => 
          combinedText.includes(keyword.toLowerCase())
        );
      });
      
      if (!matchesAnyCategory) {
        uncategorized.push({
          title: product.title,
          subject: product['custom.subject'],
          vendor: product.vendor
        });
      }
    });
    
    if (uncategorized.length > 0) {
      this.log(`⚠️  Found ${uncategorized.length} uncategorized elective products:`, uncategorized);
    }
    
    return uncategorized;
  }

  /**
   * Clean metafield values (handle JSON arrays and normalize Math/Mathematics)
   */
  cleanMetafieldValue(value) {
    // Type check first to prevent errors
    if (value == null) {
      return value;
    }
    
    // Convert to string if it's not already
    const stringValue = String(value);
    
    // Handle JSON arrays
    if (stringValue.startsWith('[') && stringValue.endsWith(']')) {
      try {
        const parsed = JSON.parse(stringValue);
        value = Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : value;
      } catch (e) {
        // Keep original value if parsing fails
      }
    }
    
    // Normalize Math/Mathematics terminology
    if (typeof value === 'string') {
      if (value.toLowerCase() === 'mathematics') {
        return 'Math';
      }
      if (value.toLowerCase() === 'math') {
        return 'Math';
      }
    }
    
    return value;
  }
  /**
   * Calculate impact score for auto-ordering
   */
  calculateImpact(filterDef, products) {
    const options = this.getFilterOptions(filterDef, products, {});
    if (options.length === 0) return 0;
    
    // Calculate average reduction potential
    const reductions = options.map(option => {
      const filtered = this.simpleFilter(products, { [filterDef.key]: option.value });
      return 1 - (filtered.length / products.length);
    });
    
    return reductions.reduce((sum, r) => sum + r, 0) / reductions.length;
  }
  /**
   * Simple filtering for impact calculation with Full Access support
   */
  simpleFilter(products, filters) {
    return products.filter(product => {
      return Object.keys(filters).every(filterKey => {
        const filterValue = filters[filterKey];
        
        // Enhanced Full Access handling for impact calculation
        if (filterKey === 'custom.subject' && this.isEnhancedFullAccessProduct(product)) {
          return true;
        }
        
        // Tag filtering
        if (filterKey === 'tags') {
          return product.tags && product.tags.some(tag => 
            tag.toLowerCase().includes(filterValue.toLowerCase())
          );
        }
        
        // Standard metafield filtering
        const productValue = product[filterKey];
        return productValue && 
               productValue.toString().toLowerCase().includes(filterValue.toLowerCase());
      });
    });
  }
  /**
   * Check if product is Full Access with enhanced vendor restrictions
   */
  isFullAccessProduct(product) {
    const productType = product['custom.product_type'] || product.productType;
    const vendor = product.vendor || '';
    
    // Full Access products must be from Monarch
    const isMonarch = vendor.toLowerCase().includes('monarch');
    const isFullAccessType = productType && productType.toLowerCase().includes('full access');
    
    return isMonarch && isFullAccessType;
  }

  /**
   * Enhanced Full Access detection with comprehensive business logic
   */
  isEnhancedFullAccessProduct(product) {
    const productType = product['custom.product_type'] || product.productType || '';
    const title = product.title || '';
    const vendor = product.vendor || '';
    
    // Must be Monarch vendor
    if (!vendor.toLowerCase().includes('monarch')) {
      return false;
    }
    
    // Check for Full Access indicators in product type or title
    const fullAccessIndicators = [
      'full access',
      'complete access',
      'all subjects',
      'unlimited access'
    ];
    
    const productTypeMatch = fullAccessIndicators.some(indicator => 
      productType.toLowerCase().includes(indicator)
    );
    
    const titleMatch = fullAccessIndicators.some(indicator => 
      title.toLowerCase().includes(indicator)
    );
    
    return productTypeMatch || titleMatch;
  }  /**
   * Main filtering method with enhanced Full Access business logic and depth-aware caching
   */
  getFilteredProducts(allProducts, currentFilters) {
    // Create depth-aware cache key
    const filterDepth = this.filterHistory.length;
    const cacheKey = `filtered_${JSON.stringify(currentFilters)}_depth_${filterDepth}`;
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }
    
    const filtered = allProducts.filter(product => {
      return Object.keys(currentFilters).every(filterKey => {
        const filterValue = currentFilters[filterKey];
        
        // Skip optional filters - don't filter by this
        if (filterKey === 'skip_optional_filters') {
          return true;
        }
        
        // ENHANCED BUSINESS LOGIC: Full Access products match ANY subject
        if (filterKey === 'custom.subject' && this.isEnhancedFullAccessProduct(product)) {
          this.log(`Full Access product "${product.title}" matches any subject filter`);
          return true;
        }
        
        // ENHANCED BUSINESS LOGIC: Full Access products match multiple grade levels
        if (filterKey === 'custom.grade_level' && this.isEnhancedFullAccessProduct(product)) {
          // Full Access typically covers grades 3-12
          const gradeFilter = filterValue.toLowerCase();
          const fullAccessGrades = ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
          
          if (fullAccessGrades.some(grade => gradeFilter.includes(grade))) {
            this.log(`Full Access product "${product.title}" matches grade filter`);
            return true;
          }        }
        
        // Tag filtering - enhanced to support multiple tag filters
        if (filterKey === 'tags') {
          // Support both single string and array of tags
          const tagFilters = Array.isArray(filterValue) ? filterValue : [filterValue];
          
          // Product must match ALL applied tag filters (AND logic)
          return tagFilters.every(tagFilter => 
            product.tags && product.tags.some(tag => 
              tag.toLowerCase().includes(tagFilter.toLowerCase())
            )
          );
        }
        
        // Vendor filtering with Full Access special handling
        if (filterKey === 'vendor') {
          if (this.isEnhancedFullAccessProduct(product)) {
            // Full Access products should appear under Monarch searches
            return filterValue.toLowerCase().includes('monarch');
          }
          return product.vendor && 
                 product.vendor.toLowerCase().includes(filterValue.toLowerCase());
        }
          // Elective category filtering
        if (filterKey === 'custom.elective_category') {
          return this.matchesElectiveCategory(product, filterValue);
        }
        
        // Standard metafield filtering
        const productValue = product[filterKey];
        if (!productValue) return false;
        
        const cleanProductValue = this.cleanMetafieldValue(productValue.toString());
        return cleanProductValue && 
               cleanProductValue.toLowerCase().includes(filterValue.toLowerCase());
      });
    });
    
    this.cacheSet(cacheKey, filtered);
    return filtered;
  }

  /**
   * Render filter step UI
   */
  renderFilterStep(stepData, products, ui) {
    const { nextFilter, filterDropdownButton, filterDropdownMenu, selectDropdownOption } = ui;
    
    nextFilter.classList.remove('hidden');
      // Update button text
    const dropdownText = filterDropdownButton.querySelector('.dropdown-text');
    if (dropdownText) {
      if (stepData.type === 'more_filters') {
        dropdownText.textContent = `${stepData.label} (Optional)...`;
      } else {
        dropdownText.textContent = `Choose ${stepData.label}...`;
      }
    }
    
    // Clear and populate dropdown
    filterDropdownMenu.innerHTML = '';
      // Handle curriculum descriptions for enhanced UX
    const isCurriculumFilter = stepData.type === 'curriculum';
    const isElectiveCategoryFilter = stepData.type === 'elective_category';
    
    stepData.options.forEach(option => {
      const optionElement = document.createElement('div');
      optionElement.className = 'filter-dropdown-option';
      optionElement.setAttribute('tabindex', '0');
      optionElement.setAttribute('role', 'option');
        if (isCurriculumFilter) {
        this.renderCurriculumOption(optionElement, option);
      } else if (isElectiveCategoryFilter) {
        this.renderElectiveCategoryOption(optionElement, option);
      } else {
        this.renderStandardOption(optionElement, option);
      }
      
      // Handle skip option for Optional Filters
      optionElement.addEventListener('click', () => {
        if (option.isSkipOption) {
          // Skip optional filters and move to results
          selectDropdownOption('skip_optional_filters', 'skip_optional_filters');
        } else {
          const filterKey = stepData.key === 'more_filters' ? 'tags' : stepData.key;
          selectDropdownOption(filterKey, option.value);
        }
      });
      
      filterDropdownMenu.appendChild(optionElement);
    });
  }
  /**
   * Render curriculum option with enhanced descriptions including Full Access info
   */
  renderCurriculumOption(element, option) {
    const descriptions = {
      'Monarch': 'Online, self-paced (grades 3-12) • Full Access available',
      'LIFEPAC': 'Print worktext, mastery-based (K-12)',
      'Horizons': 'Print, spiral learning, hands-on (Pre K-12)',
      'Resources': 'Supplementary teaching materials'
    };
    
    const description = descriptions[option.value] || '';
    
    element.innerHTML = `
      <div class="option-content">
        <div class="option-main">
          <span class="option-text">${option.value}</span>
          <span class="dropdown-option-count">${option.count}</span>
        </div>
        ${description ? `<div class="option-description">${description}</div>` : ''}
      </div>
    `;    element.classList.add('enhanced-curriculum-option');
  }

  /**
   * Render elective category option with descriptions
   */
  renderElectiveCategoryOption(element, option) {
    // Use the description from the option data (which comes from electiveCategories mapping)
    const description = option.description || '';
    
    element.innerHTML = `
      <div class="option-content">
        <div class="option-main">
          <span class="option-text">${option.value}</span>
          <span class="dropdown-option-count">${option.count}</span>
        </div>
        ${description ? `<div class="option-description">${description}</div>` : ''}
      </div>
    `;
    element.classList.add('enhanced-curriculum-option'); // Reuse same styling as curriculum
  }

  /**
   * Render standard option
   */
  renderStandardOption(element, option) {
    element.innerHTML = `
      <span class="option-text">${option.label || option.value}</span>
      <span class="dropdown-option-count">${option.count}</span>
    `;
  }/**
   * Check for results display with smart threshold logic
   */
  checkForResults(context) {
    const { finalResults, finalContent, allProducts, currentFilters, buildFilterUrl, onClose } = context;
    
    try {
      // DEBUG: Log current filters to track skip flag issues
      this.log(`🔍 Checking results with filters:`, currentFilters);
      
      // Validate required UI elements
      if (!finalResults || !finalContent) {
        this.log('Warning: Missing required UI elements for results display');
        return false;
      }
      
      const filteredProducts = this.getFilteredProducts(allProducts, currentFilters);
      const hasFilters = Object.keys(currentFilters).length > 0;
      const stepCount = this.stepHistory.length;
      
      // Check if more filtering options are available
      const nextStepData = this.getNextFilterStep(filteredProducts, currentFilters, stepCount);
      const hasMoreFilterOptions = nextStepData !== null && stepCount < this.config.maxSteps;
      
      this.log(`Check results: ${filteredProducts.length} products, ${stepCount} steps, hasFilters: ${hasFilters}, hasMoreOptions: ${hasMoreFilterOptions}`, currentFilters);
        // SMART THRESHOLD LOGIC:
      // Show results if:
      // 1. ≤5 products OR
      // 2. No more filtering options available OR
      // 3. Skip option selected (user explicitly chose to skip optional filters) OR
      // 4. Reached absolute max steps
      const shouldShowResults = hasFilters && (
        filteredProducts.length <= this.config.targetResults ||
        !hasMoreFilterOptions ||
        currentFilters.skip_optional_filters === 'skip_optional_filters' ||
        stepCount >= this.config.maxSteps
      );
      
      if (shouldShowResults) {
        // Force display with multiple approaches
        finalResults.style.display = 'block';
        finalResults.style.visibility = 'visible';        finalResults.classList.remove('hidden');
        finalResults.classList.add('show');
        
        this.renderResults(filteredProducts, currentFilters, { finalResults, finalContent, buildFilterUrl, onClose });
          // Log the reason for showing results
        let reason = '';
        if (filteredProducts.length <= this.config.targetResults) reason = `≤${this.config.targetResults} products`;
        else if (!hasMoreFilterOptions) reason = 'no more filter options';
        else if (currentFilters.skip_optional_filters === 'skip') {
          reason = 'skip option selected';
          this.log(`🚨 Skip flag detected in currentFilters:`, currentFilters);
        }
        else if (stepCount >= this.config.maxSteps) reason = 'max steps reached';
        
        this.log(`Results displayed successfully - Reason: ${reason}`);
        
        // Scroll to results if needed
        setTimeout(() => {
          finalResults.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
        
        return true;
      }
      
      // Hide results
      finalResults.classList.add('hidden');
      finalResults.classList.remove('show');
      finalResults.style.display = 'none';
      return false;
      
    } catch (error) {
      this.log('Error in checkForResults', error);
      this.handleError(error, context);
      
      // Fallback: ensure results are hidden on error
      if (finalResults) {
        finalResults.classList.add('hidden');
        finalResults.style.display = 'none';
      }
      return false;
    }
  }
  /**
   * Enhanced results rendering with "See More Products" links and close functionality
   */  renderResults(products, currentFilters, ui) {
    const { finalResults, finalContent, buildFilterUrl, onClose } = ui;
    
    finalResults.classList.remove('hidden');
    
    // Apply smaller modal width for single product displays
    if (products.length === 1) {
      finalResults.style.maxWidth = 'min(450px, 90vw)';
      finalResults.style.width = 'min(450px, 90vw)';
    } else {
      // Reset to normal modal width for multiple products
      finalResults.style.maxWidth = '';
      finalResults.style.width = '';
    }
    
    // Add close button to modal
    const closeButtonHtml = `
      <div class="modal-close-container" style="position: absolute; top: 10px; right: 15px; z-index: 10;">
        <button class="modal-close-btn" aria-label="Close results and continue filtering" 
                style="background: none; border: none; font-size: 24px; cursor: pointer; color: var(--spf-text-color); 
                       padding: 5px; border-radius: 50%; transition: background-color 0.2s;">
          <i class="fas fa-times"></i>
        </button>
      </div>
    `;
      if (products.length === 1) {
      this.renderSingleProduct(products[0], currentFilters, finalContent, buildFilterUrl, closeButtonHtml);
    } else {
      this.renderMultipleProducts(products, currentFilters, finalContent, buildFilterUrl, closeButtonHtml);
    }
    
    // Attach close functionality
    this.attachCloseHandler(finalResults, onClose);
    
    // Add window resize listener for responsive layout updates
    const resizeHandler = () => {
      const isMobile = window.innerWidth <= 768;
      const isSmallScreen = window.innerWidth <= 480;
      
      // Re-render the products list with updated layout when window is resized
      if (products.length > 1) {
        // Only re-render multiple products layout (single product doesn't need grid changes)
        this.renderMultipleProducts(products, currentFilters, finalContent, buildFilterUrl, closeButtonHtml);
        // Re-attach close handler after re-rendering
        this.attachCloseHandler(finalResults, onClose);
      }
    };
    
    // Store reference to remove listener when modal closes
    if (!finalResults._resizeHandler) {
      finalResults._resizeHandler = resizeHandler;
      window.addEventListener('resize', resizeHandler);
    }
    
    // Enhanced logging for results display
    this.log(`📊 Results Modal Displayed:`, {
      productsShown: products.length,
      filterCount: Object.keys(currentFilters).length,
      currentFilters: currentFilters,
      timestamp: new Date().toLocaleTimeString()
    });
  }  /**
   * Render single product result with Full Access highlighting
   */
  renderSingleProduct(product, currentFilters, container, buildFilterUrl, closeButtonHtml = '') {
    const isFullAccess = this.isEnhancedFullAccessProduct(product);
    const subjectInfo = product['custom.subject'] ? `<strong>Subject:</strong> ${product['custom.subject']}<br>` : '';
    const gradeInfo = product['custom.grade_level'] ? `<strong>Grade Level:</strong> ${this.cleanMetafieldValue(product['custom.grade_level'])}<br>` : '';
    const vendorInfo = product.vendor ? `<strong>Curriculum:</strong> ${product.vendor}` : '';
    
    // Enhanced display for Full Access products
    const fullAccessBadge = isFullAccess ? `
      <div class="full-access-badge" style="background: #777777; color: white; padding: 0.5rem 1rem; border-radius: 20px; font-size: 0.85em; font-weight: 600; margin-bottom: 1rem; display: inline-block;">
        ⭐ MONARCH FULL ACCESS</div><br>
    ` : '';
    
    const seeMoreUrl = this.buildSeeMoreUrl(currentFilters, [product]);
    
    container.innerHTML = `
      ${closeButtonHtml}
      <div style="text-align: center;">
        <h4>Perfect Match!</h4>
        <div class="product-card">
          ${fullAccessBadge}
          <h5>${product.title || 'Product'}</h5>
          <div class="product-info">
            ${subjectInfo}${gradeInfo}${vendorInfo}
          </div>
          <div style="margin-top: calc(var(--spf-spacing) * 0.75);">
            <a href="${product.url || '#'}" class="product-link" style="display: inline-flex; margin: 0 auto;">
              View Product
            </a>
          </div>
          ${seeMoreUrl ? `
            <div style="margin-top: calc(var(--spf-spacing) * 0.5);">
              <a href="${seeMoreUrl}" class="see-more-link" style="display: inline-flex; margin: 0 auto; font-size: 0.9em;">
                See More Products
              </a>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }  /**
   * Render multiple products result with Full Access highlighting and 2-column layout for >5 products
   */  renderMultipleProducts(products, currentFilters, container, buildFilterUrl, closeButtonHtml = '') {
    const stepCount = this.stepHistory.length;
    const showAllProducts = stepCount >= this.config.maxSteps;    const isMobile = window.innerWidth <= 768;
    const isSmallScreen = window.innerWidth <= 480;
    
    let productCards = '';
    // Show all products when displaying results modal (not just at max steps)
    const productsToShow = products;
    
    // Sort products to show Full Access first
    const sortedProducts = productsToShow.sort((a, b) => {
      const aIsFullAccess = this.isEnhancedFullAccessProduct(a);
      const bIsFullAccess = this.isEnhancedFullAccessProduct(b);
      if (aIsFullAccess && !bIsFullAccess) return -1;
      if (!aIsFullAccess && bIsFullAccess) return 1;
      return 0;
    });
    
    // Use 2-column layout only on desktop with more than 3 products
    // Force single column on mobile (768px and below) and small screens (480px and below)
    const gridClass = products.length > 3 && !isMobile && !isSmallScreen ? 
      'style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--spf-spacing); margin: var(--spf-spacing) 0;"' : 
      (isMobile || isSmallScreen ? 'style="display: flex; flex-direction: column; gap: var(--spf-spacing); margin: var(--spf-spacing) 0;"' : '');
    
    sortedProducts.forEach(product => {
      const isFullAccess = this.isEnhancedFullAccessProduct(product);
      const subjectInfo = product['custom.subject'] ? `<strong>Subject:</strong> ${product['custom.subject']}<br>` : '';
      const gradeInfo = product['custom.grade_level'] ? `<strong>Grade Level:</strong> ${this.cleanMetafieldValue(product['custom.grade_level'])}<br>` : '';
      const vendorInfo = product.vendor ? `<strong>Curriculum:</strong> ${product.vendor}` : '';
      
      // Enhanced styling for Full Access products
      const fullAccessBadge = isFullAccess ? `
        <div class="full-access-badge" style="background: #777777; color: white; padding: 0.25rem 0.75rem; border-radius: 15px; font-size: 0.75em; font-weight: 600; margin-bottom: 0.5rem; display: inline-block;">
          ⭐ FULL ACCESS
        </div><br>
      ` : '';
      
      const cardStyle = isFullAccess ? 
        'text-align: center; border: 2px solid #FBB03B; background: linear-gradient(135deg, #f8faff, #f0f4ff);' : 
        'text-align: center;';
      
      productCards += `
        <div class="product-card" style="${cardStyle}">
          ${fullAccessBadge}
          <h5>${product.title || 'Product'}</h5>
          <div class="product-info">
            ${subjectInfo}${gradeInfo}${vendorInfo}
          </div>
          <div style="margin-top: calc(var(--spf-spacing) * 0.75);">
            <a href="${product.url || '#'}" class="product-link" style="display: inline-flex; margin: 0 auto;">
              View Product
            </a>
          </div>
        </div>
      `;    });
    
    const seeMoreUrl = this.buildSeeMoreUrl(currentFilters, products);
    const seeMoreSection = seeMoreUrl ? `
      <div style="text-align: center; margin-top: calc(var(--spf-spacing) * 1.5); grid-column: 1 / -1;">
        <a href="${seeMoreUrl}" class="see-more-link" style="display: inline-flex; margin: 0 auto;">
          See More Products
        </a>
      </div>
    ` : '';

    container.innerHTML = `
      ${closeButtonHtml}
      <div style="text-align: center;">
        <h4>${products.length} Product${products.length !== 1 ? 's' : ''} Found</h4>
        <div ${gridClass}>
          ${productCards}
          ${seeMoreSection}
        </div>
      </div>
    `;
  }  /**
   * Build "See More Products" URL targeting curriculum-specific collections
   */  buildSeeMoreUrl(currentFilters, products = []) {
    const filterKeys = Object.keys(currentFilters).slice(0, 3);
    if (filterKeys.length === 0) return null;
    
    // Check if we have exactly 1 Full Access product - if so, direct to monarch collection
    const fullAccessProducts = products.filter(product => this.isEnhancedFullAccessProduct(product));
    if (fullAccessProducts.length === 1) {
      return 'https://aop.com/collections/monarch';
    }
    
    const params = new URLSearchParams();
    let targetCollection = 'all';
    
    // Check if we have a curriculum filter to target specific collection
    if (currentFilters.vendor) {
      const vendor = currentFilters.vendor.toLowerCase();
      // Map vendor to collection handles (customize as needed)
      const collectionMap = {
        'alpha omega publications': 'lifepac',
        'aop': 'lifepac',
        'lifepac': 'lifepac',
        'switched on schoolhouse': 'switched-on-schoolhouse',
        'sos': 'switched-on-schoolhouse',
        'monarch': 'monarch',
        'horizons': 'horizons'
      };
      targetCollection = collectionMap[vendor] || 'all';
    }
    
    filterKeys.forEach(key => {
      const value = currentFilters[key];
      if (key === 'tags') {
        params.append('filter.p.tag', value);
      } else if (key.startsWith('custom.')) {
        params.append(`filter.p.m.${key}`, value);
      } else {
        params.append(`filter.p.${key}`, value);
      }
    });
    
    return `/collections/${targetCollection}?${params.toString()}`;
  }
  /**
   * Enhanced error handling and recovery with fallback strategies
   */
  handleError(error, context) {
    this.log('Handling error', error.message);
    
    if (!this.errorRecoveryMode) {
      this.errorRecoveryMode = true;
      
      // Try fallback filters based on context
      const { nextFilter, allProducts, currentFilters } = context;
      
      try {
        // Clear any problematic UI states
        if (nextFilter) {
          nextFilter.classList.add('hidden');
        }
        
        // Attempt to show basic results if we have some filters
        if (Object.keys(currentFilters).length > 0) {
          const safeProducts = this.getBasicFilteredProducts(allProducts, currentFilters);
          if (safeProducts.length > 0) {
            this.log(`Error recovery: Found ${safeProducts.length} products with basic filtering`);
          }
        }
        
        // Clear step history to allow restart
        this.stepHistory = [];
        
      } catch (recoveryError) {
        this.log('Error in recovery process', recoveryError.message);
        // Final fallback: reset everything
        this.clearCache();
      }
    }
  }

  /**
   * Basic filtering without advanced Full Access logic for error recovery
   */
  getBasicFilteredProducts(allProducts, currentFilters) {
    try {
      return allProducts.filter(product => {
        return Object.keys(currentFilters).every(filterKey => {
          const filterValue = currentFilters[filterKey];
          
          // Simple tag filtering
          if (filterKey === 'tags') {
            return product.tags && product.tags.some(tag => 
              tag.toLowerCase().includes(filterValue.toLowerCase())
            );
          }
          
          // Simple metafield filtering
          const productValue = product[filterKey];
          return productValue && 
                 productValue.toString().toLowerCase().includes(filterValue.toLowerCase());
        });
      });
    } catch (error) {
      this.log('Error in basic filtering', error.message);
      return allProducts; // Ultimate fallback: return all products
    }
  }

  /**
   * Get navigation steps for UI delegation
   */
  getNavigationSteps() {
    return this.config.buttonOrder.map(filter => ({
      key: filter.key,
      label: filter.label,
      priority: filter.priority,
      type: filter.type
    }));
  }
  /**
   * Check if next step has options with smart threshold logic
   */
  getNextStepHasOptions(allProducts, currentFilters, currentStep, navSteps, getFilteredProducts) {
    const filteredProducts = getFilteredProducts();
    
    // If we have ≤5 products, no need for more filtering
    if (filteredProducts.length <= this.config.targetResults) {
      this.log('No more options needed - target results reached');
      return false;
    }
    
    // Check if there are actually more filter options available
    const nextStepData = this.getNextFilterStep(filteredProducts, currentFilters, this.stepHistory.length);
    const hasMoreOptions = nextStepData !== null;
    
    this.log(`Next step options available: ${hasMoreOptions}, current step: ${this.stepHistory.length}`);
    
    return hasMoreOptions;
  }

  /**
   * Validate single option filters
   */
  isValidSingleOption(filterDef, option) {
    // Allow single options for certain filter types
    const allowSingleOption = ['curriculum', 'more_filters'];
    return allowSingleOption.includes(filterDef.type) && option.count > 1;
  }
  /**
   * Smart Cache Invalidation System
   */
  
  /**
   * Invalidate cache entries from a specific filter depth level onwards
   * This is used when backtracking to ensure stale cached data is cleared
   */
  invalidateCacheFromLevel(level) {
    this.log(`Invalidating cache from level ${level}`);
    
    const keysToDelete = [];
    for (const [key] of this.cache) {
      // Check if this cache key is from the specified level or deeper
      const depthMatch = key.match(/_depth_(\d+)/);
      if (depthMatch) {
        const keyDepth = parseInt(depthMatch[1]);
        if (keyDepth >= level) {
          keysToDelete.push(key);
        }
      }
    }
    
    // Delete invalidated cache entries
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.log(`Deleted cache key: ${key}`);
    });
    
    this.log(`Invalidated ${keysToDelete.length} cache entries from level ${level}`);
  }
    /**
   * Add a filter and track it in the filter history for cache management
   */
  addFilter(filterKey, filterValue, currentFilters) {
    // Add to filter history for depth tracking
    this.filterHistory.push({
      key: filterKey,
      value: filterValue,
      level: this.filterHistory.length
    });
    
    // Update cache invalidation level
    this.cacheInvalidationLevel = this.filterHistory.length;
    
    this.log(`Added filter: ${filterKey}=${filterValue} at level ${this.cacheInvalidationLevel}`);
    
    // Return updated filters
    const updatedFilters = { ...currentFilters };
    
    // SPECIAL HANDLING FOR TAG FILTERS: Combine multiple tag values instead of overwriting
    if (filterKey === 'tags') {
      const existingTags = updatedFilters[filterKey];
      
      if (existingTags) {
        // Convert existing tags to array if it's a string
        const existingTagsArray = Array.isArray(existingTags) ? existingTags : [existingTags];
        
        // Add new tag value if not already present
        if (!existingTagsArray.includes(filterValue)) {
          updatedFilters[filterKey] = [...existingTagsArray, filterValue];
          this.log(`🏷️ Combined tags: ${existingTagsArray} + ${filterValue} = ${updatedFilters[filterKey]}`);
        } else {
          this.log(`🏷️ Tag ${filterValue} already exists in filter, skipping duplicate`);
        }
      } else {
        // First tag filter - start with array format for consistency
        updatedFilters[filterKey] = [filterValue];
        this.log(`🏷️ First tag filter: ${filterValue}`);
      }
    } else {
      // Standard behavior for non-tag filters
      updatedFilters[filterKey] = filterValue;
    }
    
    return updatedFilters;
  }
  
  /**
   * Remove a filter (backtracking) and invalidate cache from that level
   */  removeFilter(filterKey, currentFilters) {
    // Find the filter in history
    const filterIndex = this.filterHistory.findIndex(f => f.key === filterKey);
    
    if (filterIndex !== -1) {
      const filterLevel = this.filterHistory[filterIndex].level;
      
      // Remove from filter history (everything from this level onwards)
      this.filterHistory = this.filterHistory.slice(0, filterIndex);
      
      // CRITICAL FIX: Also update step history to match filter history
      // This ensures step tracking stays in sync during backtracking
      if (this.stepHistory.length > filterIndex) {
        this.stepHistory = this.stepHistory.slice(0, filterIndex);
        this.log(`Step history updated: removed steps from index ${filterIndex}, now ${this.stepHistory.length} steps`);
      }
      
      // Invalidate cache from this level onwards
      this.invalidateCacheFromLevel(filterLevel);
      
      // Update cache invalidation level
      this.cacheInvalidationLevel = this.filterHistory.length;
      
      this.log(`Removed filter: ${filterKey} from level ${filterLevel}, cache invalidated`);
    }
    
    // Return updated filters
    const updatedFilters = { ...currentFilters };
    delete updatedFilters[filterKey];
    
    return updatedFilters;
  }
  
  /**
   * Reset all state for page refresh - complete cache and history reset
   */
  resetForPageRefresh() {
    this.log('Resetting state for page refresh');
    
    // Clear all cache
    this.cache.clear();
    
    // Reset filter history
    this.filterHistory = [];
    this.cacheInvalidationLevel = 0;
    
    // Reset other state
    this.stepHistory = [];
    this.errorRecoveryMode = false;
    this.fullAccessProductsCache = null;
    
    this.log('State reset complete');
  }

  /**
   * Cache management
   */
  cacheSet(key, value) {
    if (this.cache.size >= this.maxCacheSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(key, value);
  }  /**
   * Clear cache and reset state with performance optimizations
   */
  clearCache() {
    this.cache.clear();
    this.stepHistory = [];
    this.errorRecoveryMode = false;
    
    // Reset smart cache invalidation state
    this.filterHistory = [];
    this.cacheInvalidationLevel = 0;
    
    // Performance optimization: Pre-calculate Full Access products
    this.fullAccessProductsCache = null;
  }

  /**
   * Performance optimization: Get cached Full Access products
   */
  getFullAccessProducts(allProducts) {
    if (!this.fullAccessProductsCache) {
      this.fullAccessProductsCache = allProducts.filter(product => 
        this.isEnhancedFullAccessProduct(product)
      );
      this.log(`Cached ${this.fullAccessProductsCache.length} Full Access products`);
    }
    return this.fullAccessProductsCache;
  }
  /**
   * Get comprehensive statistics for debugging
   */
  getStatistics(allProducts, currentFilters) {
    const filteredProducts = this.getFilteredProducts(allProducts, currentFilters);
    const fullAccessProducts = this.getFullAccessProducts(allProducts);
    
    return {
      totalProducts: allProducts.length,
      filteredProducts: filteredProducts.length,
      fullAccessProducts: fullAccessProducts.length,
      currentStep: this.stepHistory.length,
      maxSteps: this.config.maxSteps,
      targetResults: this.config.targetResults,
      cacheSize: this.cache.size,
      errorRecoveryMode: this.errorRecoveryMode,
      appliedFilters: Object.keys(currentFilters),
      // Smart cache invalidation stats
      filterHistoryDepth: this.filterHistory.length,
      cacheInvalidationLevel: this.cacheInvalidationLevel,
      filterHistory: this.filterHistory
    };
  }
  
  /**
   * Attach close handler for results modal
   */
  attachCloseHandler(finalResults, onClose) {
    if (!finalResults) {
      this.log('⚠️ Cannot attach close handler - finalResults element not found');
      return;
    }

    const closeBtn = finalResults.querySelector('.modal-close-btn');
    if (!closeBtn) {
      this.log('⚠️ Cannot attach close handler - close button not found');
      return;
    }

    // Remove any existing event listeners to prevent duplicates
    const newCloseBtn = closeBtn.cloneNode(true);
    closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);

    // Add click handler
    newCloseBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      this.log('🔄 Close button clicked - initiating backtrack', {
        timestamp: new Date().toLocaleTimeString(),
        currentFilterDepth: this.filterHistory.length,
        hasBacktrackHistory: this.stepHistory.length > 0
      });      // Hide the modal
      finalResults.classList.add('hidden');
      finalResults.classList.remove('show');
      finalResults.style.display = 'none';

      // Clean up event listeners
      if (finalResults._resizeHandler) {
        window.removeEventListener('resize', finalResults._resizeHandler);
        finalResults._resizeHandler = null;
      }

      // Call the onClose callback if provided
      if (typeof onClose === 'function') {
        onClose();
      }

      // Enhanced logging for backtracking operation
      this.log('✅ Modal closed successfully - user can continue filtering', {
        modalHidden: finalResults.classList.contains('hidden'),
        readyForNextFilter: !finalResults.classList.contains('show'),
        discoveryModeActive: true
      });
    });

    // Add keyboard handler for Escape key
    const escapeHandler = (e) => {
      if (e.key === 'Escape' && !finalResults.classList.contains('hidden')) {
        e.preventDefault();
        newCloseBtn.click();
      }
    };

    // Store reference to remove later if needed
    finalResults._escapeHandler = escapeHandler;
    document.addEventListener('keydown', escapeHandler);

    // Add hover effects for better UX
    newCloseBtn.addEventListener('mouseenter', () => {
      newCloseBtn.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
    });

    newCloseBtn.addEventListener('mouseleave', () => {
      newCloseBtn.style.backgroundColor = 'transparent';
    });

    this.log('📎 Close handler attached successfully', {
      hasCloseButton: !!newCloseBtn,
      hasEscapeHandler: !!escapeHandler,
      hasOnCloseCallback: typeof onClose === 'function'
    });
  }
}

// Export for immediate use
window.SmartProductFinderBrain = SmartProductFinderBrain;